// apps/pwa-pos/src/lib/cash.ts
// Sprint 36 — Caja local multi-moneda. Mantiene la sesión activa y la cola
// local de apertura/cierre pendiente de subir a Supabase.
//
// Se eliminó dependencia de PowerSync (sprint 33). El POS usa localStorage
// para modo offline y sincroniza al reconectar vía sync-queue.

const SESSION_KEY = 'mibodega.cash.session';

/**
 * Una línea del desglose multi-moneda (apertura/cierre).
 * `tasa` y `monto_base` los completa la RPC server-side (estimado local-first).
 */
export interface LineaDesglose {
  moneda: string;
  monto: number;
  tasa?: number;
  monto_base?: number;
}

export interface SesionCaja {
  id: string;
  organization_id: string;
  sucursal_id: string;
  usuario_id: string;
  monto_inicial: number;
  /** Snapshot de organizations.moneda_base al momento de abrir. */
  moneda_base_codigo: string;
  /** Desglose del fondo de apertura por moneda. */
  desglose_apertura: LineaDesglose[];
  /** Desglose del arqueo al cerrar (populado al cerrar). */
  desglose_cierre?: LineaDesglose[] | null;
  /** Idempotency key generado por el cliente para subir la apertura. */
  idempotency_key_apertura: string;
  /** Idempotency key para el cierre (se setea al cerrar). */
  idempotency_key_cierre?: string;
  monto_final_declarado?: number;
  estado: 'ABIERTA' | 'CERRADA' | 'PENDIENTE_APERTURA' | 'PENDIENTE_CIERRE';
  fecha_apertura: string;
  fecha_cierre?: string;
}

function read(): SesionCaja | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SesionCaja> | null;
    if (!parsed || typeof parsed !== 'object') {
      try {
        localStorage.removeItem(SESSION_KEY);
      } catch {
        // ignore
      }
      return null;
    }
    // Sprint 47 — defensivo contra sesiones mal formadas (stale de builds
    // previos con schema distinto). Si falta cualquier campo requerido,
    // descartamos la sesión: el cajero debe reabrir caja. Esto evita que la
    // UI renderice "Fondo undefined 100,00" o intente cerrar/arqueo sobre
    // sesiones sin `desglose_apertura` (rompía el map de sprint 44).
    const required: (keyof SesionCaja)[] = [
      'id',
      'organization_id',
      'sucursal_id',
      'usuario_id',
      'monto_inicial',
      'moneda_base_codigo',
      'idempotency_key_apertura',
      'fecha_apertura',
      'estado',
    ];
    for (const k of required) {
      if (parsed[k] === undefined || parsed[k] === null) {
        console.warn(
          `[cash] sesión descartada: falta campo requerido "${String(k)}" (schema viejo?)`,
        );
        try {
          localStorage.removeItem(SESSION_KEY);
        } catch {
          // ignore
        }
        return null;
      }
    }
    const s = parsed as SesionCaja;
    return {
      ...s,
      desglose_apertura: s.desglose_apertura ?? [],
      desglose_cierre: s.desglose_cierre ?? null,
    };
  } catch {
    return null;
  }
}

function write(s: SesionCaja): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch (err) {
    console.warn('[cash] No se pudo persistir sesion:', err);
  }
}

/** True only on the first hydrated visit with no usable session and no prior-day block. */
export function debeOfrecerAperturaInicial(input: {
  cajaHidratada: boolean;
  haySesion: boolean;
  bloqueoAnterior: boolean;
  yaOfrecido: boolean;
  configLista: boolean;
}): boolean {
  return (
    input.cajaHidratada &&
    input.configLista &&
    !input.haySesion &&
    !input.bloqueoAnterior &&
    !input.yaOfrecido
  );
}

export function getSesionCajaActiva(_usuarioId?: string): SesionCaja | null {
  const s = read();
  if (!s) return null;
  // Solo cuenta como "caja usable para vender" si está abierta (o la
  // apertura aún no subió). PENDIENTE_CIERRE = el cajero ya cerró: no se
  // vende, y se puede reabrir turno una vez el cierre se encoló.
  return s.estado === 'ABIERTA' || s.estado === 'PENDIENTE_APERTURA' ? s : null;
}

export interface AbrirSesionInput {
  organizationId: string;
  sucursalId: string;
  usuarioId: string;
  monedaBase: string;
  desglose: LineaDesglose[];
  /** Si true, solo crea localmente sin esperar subir (cola diferida). */
  encolarSubida?: boolean;
}

/**
 * Crea la sesión de caja local con desglose multi-moneda.
 * El cálculo de monto_inicial en moneda_base se hace en cliente con
 * la misma lógica que la RPC (el server re-valida al subir).
 */
export async function abrirSesionCajaOffline(input: AbrirSesionInput): Promise<SesionCaja> {
  const activa = read();
  // Bloquear solo si el cajero aún tiene turno vendible. CERRADA y
  // PENDIENTE_CIERRE permiten reabrir: el cierre ya fue declarado (y el
  // flush sube el arqueo en paralelo). Antes PENDIENTE_CIERRE bloqueaba
  // para siempre si markSesionSincronizada no corría tras el cierre sync.
  if (activa && (activa.estado === 'ABIERTA' || activa.estado === 'PENDIENTE_APERTURA')) {
    throw new Error('Ya existe una sesión de caja abierta para este usuario');
  }
  if (!input.desglose.length) {
    throw new Error('El desglose no puede estar vacío');
  }

  const id = crypto.randomUUID();
  const idempotencyKey = crypto.randomUUID();

  // Cálculo de monto_base cliente (server re-valida con la última tasa real)
  const desgloseCompleto: LineaDesglose[] = input.desglose.map((linea) => {
    const tasa = linea.tasa ?? 1;
    return {
      ...linea,
      tasa,
      monto_base: Math.round(linea.monto * tasa * 100) / 100,
    };
  });
  const montoInicial =
    Math.round(desgloseCompleto.reduce((acc, l) => acc + (l.monto_base ?? 0), 0) * 100) / 100;

  const sesion: SesionCaja = {
    id,
    organization_id: input.organizationId,
    sucursal_id: input.sucursalId,
    usuario_id: input.usuarioId,
    monto_inicial: montoInicial,
    moneda_base_codigo: input.monedaBase,
    desglose_apertura: desgloseCompleto,
    idempotency_key_apertura: idempotencyKey,
    estado: input.encolarSubida ? 'PENDIENTE_APERTURA' : 'ABIERTA',
    fecha_apertura: new Date().toISOString(),
  };
  write(sesion);
  return sesion;
}

export interface CerrarSesionInput {
  sesionId: string;
  desglose: LineaDesglose[];
  encolarSubida?: boolean;
}

export async function cerrarSesionCajaOffline(input: CerrarSesionInput): Promise<SesionCaja> {
  const sesion = read();
  if (!sesion || sesion.id !== input.sesionId) {
    throw new Error('No hay sesion de caja abierta para cerrar');
  }

  const idempotencyKey = crypto.randomUUID();
  const desgloseApertura = sesion.desglose_apertura ?? [];
  const desgloseCompleto: LineaDesglose[] = input.desglose.map((linea) => {
    const tasa = linea.tasa ?? desgloseApertura.find((d) => d.moneda === linea.moneda)?.tasa ?? 1;
    return {
      ...linea,
      tasa,
      monto_base: Math.round(linea.monto * tasa * 100) / 100,
    };
  });
  const montoFinal =
    Math.round(desgloseCompleto.reduce((acc, l) => acc + (l.monto_base ?? 0), 0) * 100) / 100;

  sesion.desglose_cierre = desgloseCompleto;
  sesion.idempotency_key_cierre = idempotencyKey;
  sesion.monto_final_declarado = montoFinal;
  sesion.fecha_cierre = new Date().toISOString();
  sesion.estado = input.encolarSubida ? 'PENDIENTE_CIERRE' : 'CERRADA';

  write(sesion);
  return sesion;
}

/**
 * Marca la sesión como sincronizada con Supabase (sin alterar timestamps).
 */
export function markSesionSincronizada(sesionId: string, estadoFinal: 'ABIERTA' | 'CERRADA'): void {
  const sesion = read();
  if (!sesion || sesion.id !== sesionId) return;
  sesion.estado = estadoFinal;
  write(sesion);
}

/**
 * Adopts a server-side ABIERTA session as the local active session.
 * Used when local open was queued but remote already had a session
 * (YA_TIENE_SESION_ABIERTA): the POS must bind to the real server id so
 * close/arqueo and sales attach to the right row.
 */
export function adoptRemoteSesionAbierta(remote: {
  id: string;
  organization_id: string;
  sucursal_id: string;
  usuario_id: string;
  monto_inicial?: number | null;
  moneda_base_codigo?: string | null;
  desglose_apertura?: LineaDesglose[] | null;
  fecha_apertura?: string | null;
  idempotency_key_apertura?: string | null;
}): SesionCaja {
  const local = read();
  const sesion: SesionCaja = {
    id: remote.id,
    organization_id: remote.organization_id,
    sucursal_id: remote.sucursal_id,
    usuario_id: remote.usuario_id,
    monto_inicial: Number(remote.monto_inicial ?? local?.monto_inicial ?? 0),
    moneda_base_codigo: remote.moneda_base_codigo ?? local?.moneda_base_codigo ?? 'COP',
    desglose_apertura: remote.desglose_apertura ?? local?.desglose_apertura ?? [],
    idempotency_key_apertura:
      remote.idempotency_key_apertura ?? local?.idempotency_key_apertura ?? crypto.randomUUID(),
    estado: 'ABIERTA',
    fecha_apertura: remote.fecha_apertura ?? local?.fecha_apertura ?? new Date().toISOString(),
  };
  write(sesion);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('mibodega:cash-session-changed'));
  }
  return sesion;
}
